/**
 * @file src/shell/StepBar.js
 * THANH BƯỚC — dải ngang ở ĐÁY, chứa các bước nội dung của tiết.
 *
 * Trước đây file này là TopBar.js và render tab dạng segmented control ở trên. Đặt tên
 * theo VAI chứ không theo VỊ TRÍ: hai thanh đã đổi chỗ một lần rồi, và chính cái tên
 * "TopBar" render ở dưới đã sinh ra khối chú thích "ĐỔI VAI" rối rắm cũ.
 *
 * Mỗi bước gồm: SỐ THỨ TỰ (01, 02…) · tiêu đề · phụ đề. Số sinh từ vị trí trong
 * registry, tiết không phải khai — thêm/bớt/đổi thứ tự bước là số tự đánh lại.
 * Phụ đề là tuỳ chọn (`subLabelKey`); thiếu thì hàng chỉ có tiêu đề.
 *
 * Logo nằm ở mép PHẢI của chính thanh này (không phải phần tử rời như bản trước) nên
 * nó luôn thẳng hàng với các bước dù chiều cao thanh đổi.
 */

/**
 * @param {Object} o
 * @param {HTMLElement} o.root
 * @param {(k: string, v?: any) => string} o.t
 * @param {import('./registry.js').Registry<any>} o.tabs
 * @param {import('../core/events.js').Emitter} o.emitter
 * @param {string} [o.logoUrl] URL logo ở mép phải. Bỏ trống thì không render logo.
 * @param {string} [o.logoAlt]
 */
export function createStepBar(o) {
  const el = document.createElement('footer');
  el.className = 'ol-stepbar';
  o.root.appendChild(el);

  /** @type {string | null} */
  let activeId = null;

  /**
   * ĐANG BẬN — tiết đang nạp asset / dựng cảnh cho bước vừa chọn.
   * Giữ ở biến ngoài chứ không chỉ đặt thuộc tính lên nút: render() dựng LẠI toàn bộ
   * nút, nếu chỉ đặt `disabled` một lần thì bất kỳ lần render nào giữa chừng
   * (retranslate, registry đổi) cũng làm các nút mở khoá trở lại giữa lúc còn đang bận.
   */
  let busy = false;

  // Track dựng RIÊNG và giữ ngoài render(): render() xoá sạch innerHTML của track, nếu
  // logo nằm chung trong đó thì mỗi lần đổi bước là logo bị xoá rồi tải lại.
  const track = document.createElement('div');
  track.className = 'ol-steps';
  // KHÔNG đặt role="list" ở đây và role="listitem" lên từng <button>: role trên một
  // <button> GHI ĐÈ vai trò button, nút mất hẳn ngữ nghĩa tương tác — tệ hơn là không
  // đặt gì. aria-current="step" trên bước đang mở đã là mẫu chuẩn cho stepper, đủ dùng.
  el.appendChild(track);

  if (o.logoUrl) {
    const logo = document.createElement('img');
    logo.className = 'ol-stepbar__logo';
    logo.src = o.logoUrl;
    logo.alt = o.logoAlt ?? '';
    logo.decoding = 'async';
    // Khai kích thước THẬT của file để trình duyệt biết tỉ lệ trước khi ảnh về —
    // không thì thanh bị giật một nhịp lúc ảnh tải xong.
    logo.width = 1991;
    logo.height = 512;
    el.appendChild(logo);
  }

  function render() {
    const list = o.tabs.list();
    el.classList.toggle('is-empty', list.length === 0);
    track.innerHTML = '';
    if (!list.length) return;

    if (activeId === null || !list.some((s) => s.id === activeId)) {
      activeId = list[0]?.id ?? null;
    }

    list.forEach((slot, i) => {
      const d = slot.data;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ol-step';
      btn.dataset.id = slot.id;
      const on = slot.id === activeId;
      btn.classList.toggle('is-active', on);
      btn.disabled = busy;
      // aria-current="step" chứ không aria-pressed: đây là một DÃY BƯỚC, không phải
      // nút bật/tắt. aria-pressed khiến trình đọc màn hình đọc ra "nút Tổng quan, đã
      // nhấn" — sai hẳn nghĩa. Chỉ đặt trên bước đang mở, các bước khác gỡ hẳn.
      if (on) btn.setAttribute('aria-current', 'step');
      else btn.removeAttribute('aria-current');

      const num = document.createElement('span');
      num.className = 'ol-step__num';
      // padStart → "01".."09" rồi "10": một chữ số lẻ trong ô tròn trông lệch.
      num.textContent = String(i + 1).padStart(2, '0');

      const txt = document.createElement('span');
      txt.className = 'ol-step__text';
      const title = document.createElement('span');
      title.className = 'ol-step__title';
      title.textContent = o.t(d.labelKey);
      txt.appendChild(title);
      if (d.subLabelKey) {
        const sub = document.createElement('span');
        sub.className = 'ol-step__sub';
        sub.textContent = o.t(d.subLabelKey);
        txt.appendChild(sub);
      }

      btn.append(num, txt);
      btn.addEventListener('click', () => setActive(slot.id));
      track.appendChild(btn);
      // Dãy bước CUỘN NGANG được khi tiết có nhiều bước (xem .ol-steps trong
      // shell.css) — bước đang mở có thể nằm ngoài vùng thấy, nhất là khi tiết tự gọi
      // setActive() thay vì người dùng bấm. `inline/block: 'nearest'` chỉ cuộn khi
      // thật sự cần: bước đã thấy rồi thì đây là no-op, nên gọi trong render() (chạy
      // cả lúc retranslate) không gây cuộn bất ngờ.
      if (on) btn.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }

  /** @param {string} id */
  function setActive(id) {
    if (activeId === id) {
      // Bấm lại bước đang chọn vẫn phát event — tiết có thể muốn mở lại popup.
      o.tabs.get(id)?.data.onSelect?.(id, true);
      o.emitter.emit('ui:tab', { id, again: true });
      return;
    }
    activeId = id;
    render();
    o.tabs.get(id)?.data.onSelect?.(id, false);
    o.emitter.emit('ui:tab', { id, again: false });
  }

  const off = o.tabs.onChange(render);
  render();

  return {
    el,
    setActive,
    activeId: () => activeId,
    render,
    /** @param {boolean} on */
    setCompact(on) {
      el.classList.toggle('is-compact', on);
    },
    /**
     * Khoá/mở cả dãy bước trong lúc tiết nạp cảnh. Dùng CẢ `disabled` trên từng nút
     * (chặn bấm + rút khỏi thứ tự Tab, đúng nghĩa cho trạng thái bận) LẪN class để CSS
     * làm mờ. aria-busy để trình đọc màn hình biết vùng này đang thay đổi.
     * @param {boolean} on
     */
    setBusy(on) {
      busy = on;
      el.classList.toggle('is-busy', on);
      el.setAttribute('aria-busy', String(on));
      for (const b of track.querySelectorAll('button')) {
        /** @type {HTMLButtonElement} */ (b).disabled = on;
      }
    },
    dispose() {
      off();
      el.remove();
    },
  };
}
